import React, { useEffect, useRef, useState, useCallback } from 'react';
import SelfMonitorApi from '../../../api/SelfMonitorApi';
import { SupportBundleJobStatus } from '../../../types/SelfMonitor';

const POLL_INTERVAL_MS = 2000;

// Drives support_files.sh/.bat on the server: starts the job, polls its
// status, then downloads the finished archive through the admin-gated
// endpoint (the bundle is moved out of the world-readable www/static once
// the script finishes, so this download is the only way to retrieve it).
const SupportTab: React.FC = () => {
    const [status, setStatus] = useState<SupportBundleJobStatus | null>(null);
    const [starting, setStarting] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    const pollStatus = useCallback(async () => {
        const result = await SelfMonitorApi.getSupportStatus();
        setStatus(result);

        if (!result || result.status !== 'RUNNING') {
            stopPolling();
        }
    }, [stopPolling]);

    // Pick up a job already in progress - e.g. started just before a page
    // refresh, or from another admin's session.
    useEffect(() => {
        let cancelled = false;

        (async () => {
            const result = await SelfMonitorApi.getSupportStatus();

            if (cancelled) {
                return;
            }

            setStatus(result);

            if (result?.status === 'RUNNING') {
                pollRef.current = setInterval(pollStatus, POLL_INTERVAL_MS);
            }
        })();

        return () => {
            cancelled = true;
            stopPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleGenerate = async () => {
        setStarting(true);
        try {
            const result = await SelfMonitorApi.startSupportBundle();
            setStatus(result);

            if (result && result.status === 'RUNNING') {
                stopPolling();
                pollRef.current = setInterval(pollStatus, POLL_INTERVAL_MS);
            }
        } finally {
            setStarting(false);
        }
    };

    const handleDownload = async () => {
        if (!status?.id) {
            return;
        }

        setDownloading(true);
        try {
            await SelfMonitorApi.downloadSupportBundle(status.id);
        } finally {
            setDownloading(false);
        }
    };

    const running = status?.status === 'RUNNING';
    const complete = status?.status === 'COMPLETE';
    const failed = status?.status === 'FAILED';

    return (
        <div className="self-monitor-support">
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-life-ring fa-fw" aria-hidden="true" />
                    Support Files
                </div>
                <div className="card-body">
                    <p>
                        Generates a support bundle with system information, running processes, PostgreSQL
                        diagnostics, and the Tomcat, nginx and PostgreSQL logs. This can take a few minutes on
                        a busy repository.
                    </p>

                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={handleGenerate}
                        disabled={starting || running}
                    >
                        <i
                            className={`fal fa-fw ${starting || running ? 'fa-spinner fa-spin' : 'fa-file-zipper'}`}
                            aria-hidden="true"
                        />
                        {running ? 'Generating...' : 'Download Support Files'}
                    </button>

                    {status && status.status !== 'NONE' && (
                        <div className="self-monitor-support__status mt-3">
                            <div>
                                Status: <strong>{status.status}</strong>
                                {status.alreadyrunning && ' (a bundle was already being generated)'}
                            </div>

                            {failed && status.error && (
                                <div className="text-danger self-monitor-support__error">{status.error}</div>
                            )}

                            {complete && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-success mt-2"
                                    onClick={handleDownload}
                                    disabled={downloading}
                                >
                                    <i
                                        className={`fal fa-fw ${downloading ? 'fa-spinner fa-spin' : 'fa-download'}`}
                                        aria-hidden="true"
                                    />
                                    {downloading ? 'Downloading...' : 'Download Archive'}
                                </button>
                            )}

                            {status.output && (
                                <pre className="self-monitor-support__output">{status.output}</pre>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SupportTab;
